import * as Dialog from "@radix-ui/react-dialog";
import { useState, useRef, useEffect } from "react";
import { jsPDF } from "jspdf";
import { TERMS_CONTENT, TERMS_SECTIONS, TermsSection, TermsSubsection } from "./termsContent";

interface TermsDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
}

export default function TermsDialog({ open, onClose, onAccept }: TermsDialogProps) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setHasScrolledToBottom(false);
    }
  }, [open]);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setHasScrolledToBottom(true);
      }
    }
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    const margin = 20;
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const textWidth = pageWidth - margin * 2;
    let yPos = 20;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Terms & Conditions and Confidentiality Agreement", pageWidth / 2, yPos, {
      align: "center",
    });
    yPos += 10;

    doc.setFontSize(12);
    doc.text("FixFinder Inc. — Research Portal", pageWidth / 2, yPos, { align: "center" });
    yPos += 5;
    doc.text("Version 1.1", pageWidth / 2, yPos, { align: "center" });
    yPos += 15;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    const lines = TERMS_CONTENT.split("\n");
    lines.forEach((line: string) => {
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }

      if (line.match(/^\d+\./)) {
        doc.setFont("helvetica", "bold");
        yPos += 5;
      } else {
        doc.setFont("helvetica", "normal");
      }

      const splitText = doc.splitTextToSize(line, textWidth);
      splitText.forEach((textLine: string) => {
        if (yPos > pageHeight - 30) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(textLine, margin, yPos);
        yPos += 5;
      });
    });

    doc.save("FixFinder-Terms-and-Conditions-v2.1.pdf");
  };

  return (
    <Dialog.Root open={open} onOpenChange={() => {}}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" />
        <Dialog.Content 
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-2xl max-h-[85vh] bg-[#0A1628] rounded-2xl shadow-2xl border border-white/10 z-50 flex flex-col"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <Dialog.Title className="text-xl font-bold text-white">
              Terms & Conditions (v2.1)
            </Dialog.Title>
            <button
              onClick={downloadPDF}
              className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </button>
          </div>

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-6 text-gray-300 text-sm space-y-4"
          >
            <p className="text-gray-400 mb-4">
              FixFinder Inc. — Research Portal
            </p>
            <p className="text-gray-400 italic mb-6">
              By checking the terms checkbox, you acknowledge that you have read, understood, and
              agree to be bound by this Agreement.
            </p>

            {TERMS_SECTIONS.map((section: TermsSection, index: number) => (
              <div key={index} className="mb-6">
                <h3 className="text-white font-semibold mb-3">{section.title}</h3>
                {section.subsections.map((subsection: TermsSubsection, subIndex: number) => (
                  <div key={subIndex} className="mb-4">
                    {subsection.heading && (
                      <h4 className="text-gray-200 font-medium mb-2">{subsection.heading}</h4>
                    )}
                    <ul className="list-disc pl-5 space-y-2">
                      {subsection.items.map((item: string, itemIndex: number) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="p-6 border-t border-white/10 flex items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">
              {hasScrolledToBottom ? (
                <span className="text-green-400">✓ You've reviewed the terms</span>
              ) : (
                "Please scroll to review all terms"
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onAccept}
                disabled={!hasScrolledToBottom}
                className="px-4 py-2 bg-[#0193F8] hover:bg-[#0173C8] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                I Agree
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
